import React, { useState, useEffect } from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface ActiveCompsControlProps {
  /** Starting value shown in the input */
  initialValue?: number;
  /** Emit sanitized integer when valid */
  onChange: (value: number) => void;
  /** Bounds & step */
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Themed numeric control for "Active Comps" (integer).
 * Automatically adapts to light/dark system theme.
 */
export const ActiveCompsControl: React.FC<ActiveCompsControlProps> = ({
  initialValue = 250,
  onChange,
  min = 0,
  max = 500000,
  step = 1,
}) => {
  const theme = useSystemTheme();

  // Pull CSS variables dynamically to match the theme
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const inputBg = css.getPropertyValue("--input-bg").trim() || "var(--card-bg)";
  const labelColor = css.getPropertyValue("--muted-text-color").trim() || textColor;

  const [value, setValue] = useState<number>(initialValue);
  const [error, setError] = useState<string>("");

  // Keep local state in sync with external resets
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Validate and emit
  useEffect(() => {
    if (value < min) {
      setError(`Must be ≥ ${min}`);
    } else if (value > max) {
      setError(`Must be ≤ ${max}`);
    } else {
      setError("");
      onChange(value);
    }
  }, [value, min, max, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const num = parseInt(raw, 10);
    setValue(isNaN(num) ? 0 : num);
  };

  const inputClass =
    "w-full text-center rounded shadow-sm focus:outline-none transition-all duration-150";
  const inputStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <Label htmlFor="activeCompsInput" className={labelClass} style={labelStyle}>
        Active Comps
      </Label>

      <Input
        id="activeCompsInput"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value.toString()}
        onChange={handleChange}
        step={step}
        className={inputClass}
        style={inputStyle}
      />

      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};